import { useState, useMemo, useCallback, useRef, type ErrorInfo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Drawer, Box, Divider } from '@mui/material';
import { useNavigationConfig } from '../../services/navigationConfig';
import { useLoggerWithContext } from '../../services/logger/loggerContext';
import { useEntity } from '../../contexts/EntityContext';
import { EntitySelector } from './EntitySelector';
import { NavigationSection } from './NavigationSection';
import { TestIds } from '../../constants';
import { useUnreadCount } from '../../pages/Notifications/hooks/useUnreadCount';
import { useFeatureFlag, FeatureFlagKey } from '../../hooks/useFeatureFlags';
import { FavoritesSection } from '../Favorites/FavoritesSection';
import { MenuSectionTitle } from '../../types';

interface NavigationSidebarProps {
  open: boolean;
  onClose: () => void;
}

const DRAWER_WIDTH = 300;

export function NavigationSidebar({ open, onClose }: NavigationSidebarProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const unreadCount = useUnreadCount();
  const favoritesEnabled = useFeatureFlag(FeatureFlagKey.PSP_UI_FAVORITES);
  const { entitledEntities } = useEntity();
  const hasMultipleEntities = entitledEntities.length > 1;
  const logger = useLoggerWithContext();
  const onBoundaryError = useCallback(
    (error: unknown, info: ErrorInfo) => {
      logger.error('FavoritesSection failed to render', {
        error: error instanceof Error ? error.message : String(error),
        componentStack: info.componentStack,
      });
    },
    [logger]
  );

  const badgeState = useMemo(
    () => ({ 'tasks-and-notifications': unreadCount !== undefined && unreadCount > 0 }),
    [unreadCount]
  );

  const navigationConfig = useNavigationConfig(badgeState);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    navigationConfig.sections.forEach((section) => {
      if (section.collapsible && section.defaultExpanded !== undefined) {
        initial[section.id] = section.defaultExpanded;
      }
    });
    return initial;
  });

  const handleDrawerEnter = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, []);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleItemClick = () => {
    // The MenuItem argument is provided to this handler by child components,
    // but it is currently unused. This allows for future enhancements if we
    // want to handle different actions based on the clicked item.
    onClose();
  };
  return (
    <Drawer
      data-testid={TestIds.NAVIGATION_SIDEBAR}
      variant="temporary"
      open={open}
      onClose={onClose}
      aria-label="Main navigation"
      ModalProps={{
        keepMounted: true,
      }}
      SlideProps={{
        onEnter: handleDrawerEnter,
      }}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: 'background.paper',
          borderRight: 1,
          borderColor: 'divider',
          marginTop: '64px', // Offset by header height
          height: 'calc(100% - 64px)', // Adjust height to fit below header
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'transparent',
          marginTop: '64px', // Offset backdrop as well
          height: 'calc(100% - 64px)',
          // Tie pointer-events to `open`: an interrupted exit transition can leave
          // the `keepMounted` backdrop at opacity 0 but pointerEvents:auto,
          // invisibly blocking all clicks. This keeps the closed state inert.
          pointerEvents: open ? 'auto' : 'none',
        },
      }}
    >
      {hasMultipleEntities && <EntitySelector />}

      <Box
        ref={scrollContainerRef}
        sx={{ overflowY: 'auto', flexGrow: 1, pb: 4, pt: !hasMultipleEntities ? 2 : 0 }}
      >
        {navigationConfig.sections.map((section, index) => (
          <Box key={section.id}>
            {index > 0 && <Divider sx={{ mx: 2, my: 1 }} />}
            <NavigationSection
              section={section}
              isExpanded={expandedSections[section.id] ?? false}
              onToggle={() => toggleSection(section.id)}
              onItemClick={handleItemClick}
            />
            {favoritesEnabled && section.title === MenuSectionTitle.ACTION_HUBS && (
              <ErrorBoundary fallback={null} onError={onBoundaryError}>
                <Divider sx={{ mx: 2, my: 1 }} />
                <FavoritesSection onNavigate={onClose} />
              </ErrorBoundary>
            )}
          </Box>
        ))}
      </Box>
    </Drawer>
  );
}
